import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl, FormGroupDirective, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { PalletLayer } from 'src/app/core/types';
import { EditablePalletLayerRow } from 'src/app/shared/helpers/pallet-layer-add.helper';
import { resolvePalletLayerTypeDisplayName } from 'src/app/shared/helpers/pallet-layer-name.helper';

export interface PalletLayerLabelView {
  name: string;
  loadCountLabel: string;
  quantity: number;
  dimensionsText: string;
}

export interface PalletLayerCountEntry {
  layerId: number;
  quantity: number;
}

@Component({
  selector: 'gl-load-pallet-arrangement',
  templateUrl: './load-pallet-arrangement.component.html',
  styleUrls: ['./load-pallet-arrangement.component.scss'],
  standalone: false,
})
export class LoadPalletArrangementComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public layers: PalletLayer[] = [];
  @Input() public loadLength: number;
  @Input() public loadWidth: number;
  @Input() public loadHeight: number;
  @Input() public loadWeight: number;
  @Input() public selectedLayerId: number | null = null;
  /** Per-option stack counts keyed by layer id (add mode). */
  @Input() public layerCounts: Record<number, number> = {};
  /** When true, shows per-layer quantity inputs for group edit instead of add flow. */
  @Input() public editMode = false;
  @Input() public editRows: EditablePalletLayerRow[] = [];
  @Input() public layersLoading = false;

  @Output() public layerSelected = new EventEmitter<PalletLayer>();
  @Output() public layerCountsChange = new EventEmitter<PalletLayerCountEntry[]>();

  public form = new UntypedFormGroup({});
  public editForm = new UntypedFormGroup({});
  @ViewChild('layersToAddForm', { static: false })
  private layersToAddFormDirective?: FormGroupDirective;

  private subscriptions: Subscription[] = [];
  private countsFormSubscription?: Subscription;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    if (!this.editMode) {
      this.rebuildAddForm();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.countsFormSubscription?.unsubscribe();
  }

  /** Non-zero layer counts for submit (call before add). */
  public getLayerCounts(): PalletLayerCountEntry[] {
    return this.layers.map((layer) => ({
      layerId: layer.id,
      quantity: this.parseCount(this.form.controls[`count_${layer.id}`]?.value),
    }));
  }

  public triggerLayersToAddValidation(): void {
    this.layersToAddFormDirective?.onSubmit(undefined);
    Object.keys(this.form.controls).forEach((key) => {
      this.form.controls[key].markAsTouched();
      this.form.controls[key].updateValueAndValidity({ emitEvent: false });
    });
  }

  public hasLayersToAddValidationErrors(): boolean {
    if (!this.layers.length) {
      return true;
    }
    const hasPositive = this.getLayerCounts().some((entry) => entry.quantity > 0);
    return this.isSelectedLayerCountMissing() || !hasPositive || this.form.invalid;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.editMode && (changes.layers || changes.editMode)) {
      this.rebuildAddForm();
    } else if (!this.editMode && changes.layerCounts && this.layers?.length) {
      this.syncAddFormFromParentCounts();
    }
    if (changes.layers && this.layers?.length && !this.editMode) {
      const stillValid = this.layers.some((l) => l.id === this.selectedLayerId);
      if (!stillValid && this.selectedLayerId != null) {
        this.selectedLayerId = null;
      }
    }
    if (!this.editMode && changes.selectedLayerId && this.form.controls) {
      this.revalidateCountControls();
    }
    if (this.editMode && (changes.editRows || changes.editMode)) {
      this.rebuildEditForm();
    }
  }

  /** Layer counts from edit form (one per edit row, in row order). */
  public getEditLayerCounts(): number[] {
    return this.editRows.map((_, index) => {
      const raw = this.editForm.controls[`row_${index}`]?.value;
      const parsed = typeof raw === 'number' ? Math.floor(raw) : parseInt(String(raw ?? ''), 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    });
  }

  public selectLayer(layer: PalletLayer): void {
    this.selectedLayerId = layer.id;
    this.layerSelected.emit(layer);
    this.revalidateCountControls();
  }

  public onLayerCountsInput(): void {
    this.layerCountsChange.emit(this.getLayerCounts());
  }

  public getLayerLabelView(
    layer: PalletLayer,
    allLayers: PalletLayer[] = this.layers,
  ): PalletLayerLabelView {
    const name = this.layerDisplayName(layer, allLayers);
    const qty = layer.quantity ?? 0;
    const dims = layer.dimensions;
    const length = Math.round(dims?.length ?? this.loadLength ?? 0);
    const width = Math.round(dims?.width ?? this.loadWidth ?? 0);
    const height = Math.round(dims?.height ?? this.loadHeight ?? 0);
    const weight = Math.round(layer.weight ?? this.loadWeight ?? 0);
    return {
      name,
      loadCountLabel: String(this.translate.instant('pallet_layer_load_count')),
      quantity: qty,
      dimensionsText: `${length} x ${width} x ${height} cm | ${weight} kg`,
    };
  }

  public getEditRowLabelView(row: EditablePalletLayerRow): PalletLayerLabelView {
    const arrangementLayers = this.editRows.map((r) => r.arrangementLayer);
    return this.getLayerLabelView(row.arrangementLayer, arrangementLayers);
  }

  private layerDisplayName(layer: PalletLayer, allLayers: PalletLayer[]): string {
    return resolvePalletLayerTypeDisplayName(layer, allLayers, (key, params) =>
      String(this.translate.instant(key, params)),
    );
  }

  private rebuildAddForm(): void {
    this.countsFormSubscription?.unsubscribe();
    const controls: Record<string, UntypedFormControl> = {};
    this.layers.forEach((layer) => {
      const fromParent = this.layerCounts?.[layer.id];
      const initial =
        typeof fromParent === 'number' && Number.isFinite(fromParent) && fromParent >= 0
          ? Math.floor(fromParent)
          : 0;
      const layerId = layer.id;
      controls[`count_${layerId}`] = new UntypedFormControl(initial, [
        Validators.required,
        Validators.min(0),
        (control) => this.selectedLayerMinCountValidator(layerId, control),
      ]);
    });
    this.form = new UntypedFormGroup(controls);
    this.countsFormSubscription = this.form.valueChanges.subscribe(() => this.onLayerCountsInput());
  }

  private syncAddFormFromParentCounts(): void {
    this.layers.forEach((layer) => {
      const control = this.form.controls[`count_${layer.id}`];
      if (!control) {
        return;
      }
      const fromParent = this.layerCounts?.[layer.id];
      if (typeof fromParent !== 'number' || !Number.isFinite(fromParent) || fromParent < 0) {
        return;
      }
      const next = Math.floor(fromParent);
      if (this.parseCount(control.value) !== next) {
        control.setValue(next, { emitEvent: false });
      }
    });
  }

  private parseCount(raw: unknown): number {
    const parsed = typeof raw === 'number' ? Math.floor(raw) : parseInt(String(raw ?? ''), 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  private selectedLayerMinCountValidator(layerId: number, control: AbstractControl): ValidationErrors | null {
    if (this.selectedLayerId !== layerId) {
      return null;
    }
    return Validators.min(1)(control);
  }

  private revalidateCountControls(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.controls[key].updateValueAndValidity({ emitEvent: false });
    });
  }

  private isSelectedLayerCountMissing(): boolean {
    if (this.selectedLayerId == null) {
      return false;
    }
    return this.parseCount(this.form.controls[`count_${this.selectedLayerId}`]?.value) < 1;
  }

  private rebuildEditForm(): void {
    const controls: Record<string, UntypedFormControl> = {};
    this.editRows.forEach((row, index) => {
      controls[`row_${index}`] = new UntypedFormControl(row.layersLoadedQuantity, [
        Validators.required,
        Validators.min(0),
      ]);
      if (!row.saveable) {
        controls[`row_${index}`].disable({ emitEvent: false });
      }
    });
    this.editForm = new UntypedFormGroup(controls);
  }
}
